import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import './App.css';
import './Configs/axiosInterceptor';

//LoginPage imports
import LoginPage from './Login/LoginPage';
import PhoneLoginPage from './Login/PhoneLoginPage';
import ForgotPasswordPage from './Login/ForgotPasswordPage';
import ResetPasswordPage from './Login/ResetPasswordPage';

//GCPortalPages imports
import GCSidebar from './GCPortal/global/Sidebar/Sidebar';
import Topbar from './GCPortal/global/Topbar/Topbar';
import GCSiteplans from './GCPortal/Pages/Siteplans/Siteplans';
import GCViewplan from './GCPortal/Pages/Viewplan/Viewplan';
import GCDeficiencyPage from './GCPortal/Pages/Deficiencies/DeficiencyPage';
import ProjectList from './GCPortal/Pages/ProjectList/ProjectList';
import ProjectDashboard from './GCPortal/Pages/ProjectDashboard/ProjectDashboard';
import ProjectSettingsPage from './GCPortal/Pages/ProjectDashboard/ProjectSettings/ProjectSettingsPage';
import GlobalSettings from './GCPortal/Pages/Settings/GlobalSettings';
import CloseoutDocsPage from './GCPortal/Pages/CloseoutDocs/CloseoutDocsPage';
import DocumentViewerPage from './GCPortal/Pages/CloseoutDocs/DocumentViewerPage';
import ReportsPage from './GCPortal/Pages/Reports/ReportsPage';
import UsageDashboardPage from './GCPortal/Pages/UsageDashboard/UsageDashboardPage';
import PMRoute from './AuthUtils/PMRoute';

// Import NotificationProvider
import { NotificationProvider } from './NotificationSystem';

// Import EditModeProvider
import { EditModeProvider, useEditMode } from './contexts/EditModeContext';

// Import AuthProvider
import { AuthProvider } from './contexts/AuthContext';

import ErrorBoundary from './components/ErrorBoundary';
import RequirePermission from './components/RequirePermission';
import { PERMISSIONS } from './utils/permissions';

// Component to extract and provide project ID from URL
const AppContent = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const location = useLocation();
    const [currentProjectName, setCurrentProjectName] = useState('');

    // Extract project ID from URL
    const extractProjectId = () => {
        const matches = location.pathname.match(/\/projects\/([^/]+)/);
        return matches ? matches[1] : null;
    };

    const currentProjectId = extractProjectId();

    // Get project name from sessionStorage when projectId changes
    useEffect(() => {
        if (!currentProjectId) {
            setCurrentProjectName('');
            return;
        }

        // Get project name from sessionStorage
        const storedProjectName = sessionStorage.getItem(`project_${currentProjectId}_name`);

        if (storedProjectName) {
            setCurrentProjectName(storedProjectName);
        } else {
            console.log('No stored name found for project ID:', currentProjectId);
            // If no stored name, we'll just use a default
            setCurrentProjectName('Project Details');
        }
    }, [currentProjectId]);

    // Check if we're on a login/auth page (public pages that don't require authentication)
    const isLoginPage =
        location.pathname === '/' ||
        location.pathname === '/phone-login' ||
        location.pathname.startsWith('/phone-login/') ||
        location.pathname === '/forgot-password' ||
        location.pathname.startsWith('/reset-password');

    const handleSidebarToggle = () => {
        setSidebarOpen(!sidebarOpen);
    };

    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        }
    }, [isMobile]);

    // Main Portal Content - all users see the same routes with permission-based feature access
    const PortalContent = () => {
        const { isEditMode } = useEditMode();

        return (
            <div className={`App ${isEditMode ? 'edit-mode' : ''}`}>
                {!isEditMode && (
                    <GCSidebar
                        open={sidebarOpen}
                        onClose={handleSidebarToggle}
                        projectId={currentProjectId}
                        projectName={currentProjectName}
                    />
                )}
                <Topbar className={isEditMode ? 'edit-mode' : ''} />

                <main className="MainContent">
                    <Routes>
                        <Route path="/projects" element={<ProjectList />} />
                        <Route path="/viewplan" element={<GCViewplan />} />
                        <Route path="/deficiencies" element={<GCDeficiencyPage />} />
                        <Route path="/projects/:projectId" element={<ProjectDashboard />} />
                        <Route
                            path="/projects/:projectId/settings"
                            element={<ProjectSettingsPage />}
                        />
                        <Route
                            path="/projects/:projectId/deficiencies"
                            element={<GCDeficiencyPage />}
                        />
                        <Route path="/projects/:projectId/plans" element={<GCSiteplans />} />
                        <Route
                            path="/projects/:projectId/plans/:floorplanId"
                            element={<GCViewplan isSidebarOpen={sidebarOpen} />}
                        />
                        <Route
                            path="/projects/:projectId/reports"
                            element={
                                <RequirePermission
                                    permission={PERMISSIONS.REPORTS_READ}
                                    scope="project"
                                    projectId={currentProjectId}
                                >
                                    <ReportsPage />
                                </RequirePermission>
                            }
                        />
                        <Route
                            path="/projects/:projectId/closeout-docs"
                            element={
                                <RequirePermission
                                    permission={PERMISSIONS.CLOSEOUT_DOCS_READ}
                                    scope="project"
                                    projectId={currentProjectId}
                                >
                                    <CloseoutDocsPage />
                                </RequirePermission>
                            }
                        />
                        <Route
                            path="/projects/:projectId/closeout-docs/view/:documentId"
                            element={<DocumentViewerPage />}
                        />
                        <Route
                            path="/usage-dashboard"
                            element={
                                <RequirePermission permission={PERMISSIONS.VIEW_USAGE_DASHBOARD}>
                                    <UsageDashboardPage />
                                </RequirePermission>
                            }
                        />
                        <Route path="/settings" element={<GlobalSettings />} />
                    </Routes>
                </main>
            </div>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <ErrorBoundary resetKey={location.pathname}>
                <AuthProvider>
                    <EditModeProvider>
                        {!isLoginPage ? (
                            <PMRoute>
                                <NotificationProvider>
                                    <PortalContent />
                                </NotificationProvider>
                            </PMRoute>
                        ) : (
                            <div>
                                <Routes>
                                    <Route path="/" element={<LoginPage />} />
                                    <Route path="/phone-login" element={<PhoneLoginPage />} />
                                    <Route
                                        path="/phone-login/:token"
                                        element={<PhoneLoginPage />}
                                    />
                                    <Route
                                        path="/forgot-password"
                                        element={<ForgotPasswordPage />}
                                    />
                                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                                </Routes>
                            </div>
                        )}
                    </EditModeProvider>
                </AuthProvider>
            </ErrorBoundary>
        </ThemeProvider>
    );
};

function App() {
    return <AppContent />;
}

export default App;
